import {
  ClerkProvider,
  RedirectToSignIn,
  SignedIn,
  SignedOut,
  useAuth,
} from '@clerk/clerk-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from 'react-router-dom';

// API Client
import { setTokenGetter } from './services/ApiClient';

// Auth Config
import { clerkConfig } from './config/auth';

// Layout
import { DashboardLayout } from './components/layouts/MainLayout';

import { Button } from './components/ui/button';
import AboutPage from './pages/AboutPage';
import AssetDetailPage from './pages/AssetDetailPage';
import AssetsPage from './pages/AssetsPage';
import BankAccountsPage from './pages/BankAccountsPage';
// Pages
import Dashboard from './pages/DashboardPage';
import QuickBooksPage from './pages/QuickBooksPage';
import { DataManagementPage } from './pages/DataManagementPage';
import DocumentsPage from './pages/DocumentsPage';
import KpiSettingsPage from './pages/KpiSettingsPage';
import LandingPage from './pages/LandingPage';
import SettingsPage from './pages/SettingsPage';
import ContactsPage from './pages/contacts';
import StatementUploadPage from './pages/StatementUploadPage';

// Auth wrapper component for protected routes
function AuthWrapper({ children }: { children: ReactNode }) {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [authError, setAuthError] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  useEffect(() => {
    if (isLoaded && isSignedIn && getToken) {
      try {
        // Set up the token getter function that will fetch fresh tokens for each API call
        setTokenGetter(async () => {
          try {
            const token = await getToken();
            return token;
          } catch (error) {
            console.error('Failed to get fresh token:', error);
            return null;
          }
        });

        setAuthError(false);
        setIsInitialized(true);
      } catch (err) {
        console.error('Token setup error:', err);
        setAuthError(true);
      }
    } else if (isLoaded && !isSignedIn) {
      // Clear token getter when signed out
      setTokenGetter(null);
      setIsInitialized(false);
    }
  }, [getToken, isLoaded, isSignedIn]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setTokenGetter(null);
    };
  }, []);

  // Show loading state while Clerk is initializing
  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  // If loaded and not signed in, redirect to sign in
  if (isLoaded && !isSignedIn) {
    return <RedirectToSignIn />;
  }

  // Show loading state while initializing after sign-in
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  // Show error state if token fetch failed
  if (authError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <div className="text-center mb-4">
          <h2 className="text-xl font-semibold mb-2">Authentication Error</h2>
          <p className="text-gray-600 mb-4">
            Unable to retrieve authentication token. Please try refreshing the
            page.
          </p>
          <Button
            onClick={() => {
              setAuthError(false);
              window.location.reload();
            }}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return children;
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
}

export const AuthTokenBootstrapper = () => {
  const { getToken, isSignedIn } = useAuth();

  useEffect(() => {
    if (isSignedIn && getToken) {
      // Set up the token getter function that will fetch fresh tokens
      setTokenGetter(async () => {
        try {
          // Call getToken with no parameters for the default template
          const token = await getToken();
          return token;
        } catch (error) {
          console.error('Token fetch error in bootstrapper:', error);
          return null;
        }
      });
    } else {
      // Clear token getter when not signed in
      setTokenGetter(null);
    }

    // Cleanup function
    return () => {
      if (!isSignedIn) {
        setTokenGetter(null);
      }
    };
  }, [getToken, isSignedIn]);

  return null;
};

// Create a QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      refetchOnWindowFocus: false,
      retry: 3, // Retry failed requests 3 times
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff with max 30 seconds
    },
  },
});

function AppRouter() {
  return (
    <ClerkProvider {...clerkConfig}>
      <AuthTokenBootstrapper />
      <QueryClientProvider client={queryClient}>
        <Router>
          <Routes>
            {/* Public pages */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/about" element={<AboutPage />} />

            {/* Protected app routes */}
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/app/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="assets" element={<AssetsPage />} />
              <Route path="assets/:slug" element={<AssetDetailPage />} />
              <Route
                path="assets/edit/:slug"
                element={<AssetDetailPage isEditing />}
              />
              <Route path="contacts" element={<ContactsPage />} />
              <Route path="bank-accounts" element={<BankAccountsPage />} />
              <Route path="quickbooks" element={<QuickBooksPage />} />
              <Route path="quick-reports" element={<DataManagementPage />} />
              <Route path="statements" element={<StatementUploadPage />} />
              <Route path="documents" element={<DocumentsPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="kpi-settings" element={<KpiSettingsPage />} />
            </Route>

            {/* Redirect old routes to new app structure */}
            <Route
              path="/dashboard"
              element={<Navigate to="/app/dashboard" replace />}
            />
            <Route
              path="/assets/*"
              element={<Navigate to="/app/assets" replace />}
            />
            <Route
              path="/contacts"
              element={<Navigate to="/app/contacts" replace />}
            />
            <Route
              path="/bank-accounts"
              element={<Navigate to="/app/bank-accounts" replace />}
            />
            <Route
              path="/quick-reports"
              element={<Navigate to="/app/quick-reports" replace />}
            />
            <Route
              path="/documents"
              element={<Navigate to="/app/documents" replace />}
            />
            <Route
              path="/settings"
              element={<Navigate to="/app/settings" replace />}
            />
            <Route
              path="/kpi-settings"
              element={<Navigate to="/app/kpi-settings" replace />}
            />
            <Route
              path="/statements"
              element={<Navigate to="/app/statements" replace />}
            />
            <Route
              path="/quickbooks"
              element={<Navigate to="/app/quickbooks" replace />}
            />
          </Routes>

          {/* Toast notifications */}
          <Toaster position="top-right" />
        </Router>
      </QueryClientProvider>
    </ClerkProvider>
  );
}

export default AppRouter;
